import { inject, InjectionToken, Pipe, PipeTransform } from '@angular/core';
import {
    FOREIGN_PHONE_INPUT_TRANSFORM_FN,
    FOREIGN_PHONE_MASK_SETTINGS,
    PHONE_INPUT_TRANSFORM_FN,
    PHONE_MASK_SETTINGS,
} from '@bazis/configuration.service';
import { NgxMaskService } from 'ngx-mask';

export type PhonePipeSettings = {
    pattern: string;
    prefix: string;
    transform?: Function;
    foreignPattern: string;
    foreignPrefix: string;
    foreignTransform?: Function;
};

export const PHONE_PIPE_DEFAULT_SETTINGS = new InjectionToken<PhonePipeSettings>('', {
    providedIn: 'root',
    factory: () => {
        return {
            pattern: PHONE_MASK_SETTINGS.mask,
            prefix: PHONE_MASK_SETTINGS.prefix,
            transform: PHONE_INPUT_TRANSFORM_FN,
            foreignPattern: FOREIGN_PHONE_MASK_SETTINGS.mask,
            foreignPrefix: FOREIGN_PHONE_MASK_SETTINGS.prefix,
            foreignTransform: FOREIGN_PHONE_INPUT_TRANSFORM_FN,
        };
    },
});

/**
 * PhonePipe formats local and foreign phone numbers for display.
 *
 * It uses `NgxMaskService` under the hood and configurable defaults
 * provided via the `PHONE_PIPE_DEFAULT_SETTINGS` injection token.
 * The defaults come from `PHONE_MASK_SETTINGS`, `FOREIGN_PHONE_MASK_SETTINGS`,
 * `PHONE_INPUT_TRANSFORM_FN`, and `FOREIGN_PHONE_INPUT_TRANSFORM_FN` in
 * `configuration.service.ts`.
 *
 * How it works:
 * - Tries the local format first: applies `pattern` and `prefix` after
 *   an optional `transform` (e.g. removing "+7").
 * - If the value length does not match the local pattern, falls back to
 *   the foreign format with `foreignPattern`, `foreignPrefix`, and
 *   optional `foreignTransform`.
 *
 * Overriding defaults (provide your own settings at app bootstrap or any provider scope):
 *
 *   providers: [
 *       {
 *           provide: PHONE_PIPE_DEFAULT_SETTINGS,
 *           useValue: {
 *               pattern: '000 000-00-00',
 *               prefix: '+7 ',
 *               transform: PHONE_INPUT_TRANSFORM_FN,
 *               foreignPattern: '000000000999999',
 *               foreignPrefix: '+',
 *               foreignTransform: FOREIGN_PHONE_INPUT_TRANSFORM_FN,
 *           },
 *       },
 *   ]
 *
 * Template usage (Angular v20+ syntax):
 *
 *   @if (user?.phone as phone) {
 *       <span>{{ phone | phone }}</span>
 *   }
 *
 *   @if (contact?.internationalPhone as intl) {
 *       <span>{{ intl | phone }}</span>
 *   }
 */
@Pipe({
    name: 'phone',
    standalone: true,
})
export class PhonePipe implements PipeTransform {
    protected maskService = inject(NgxMaskService);

    public defaultSettings = inject(PHONE_PIPE_DEFAULT_SETTINGS);

    /**
     * Formats the input phone number using local or foreign rules.
     *
     * @param value A raw phone string (may contain country prefix).
     * @param pattern Local number mask pattern (no country code), e.g. "000 000-00-00".
     * @param prefix Local prefix to prepend, e.g. "+7 ".
     * @param transformFn Optional pre-mask transform for local numbers.
     * @param foreignPattern Foreign number mask pattern.
     * @param foreignPrefix Foreign prefix to prepend, e.g. "+".
     * @param foreignTransformFn Optional pre-mask transform for foreign numbers.
     * @returns Formatted phone number string or the original value if not applicable.
     */
    transform(
        value: string,
        pattern = this.defaultSettings.pattern,
        prefix = this.defaultSettings.prefix,
        transformFn = this.defaultSettings.transform,
        foreignPattern = this.defaultSettings.foreignPattern,
        foreignPrefix = this.defaultSettings.foreignPrefix,
        foreignTransformFn = this.defaultSettings.foreignTransform,
    ): string {
        if ((!pattern && !foreignPattern) || !value) return value;

        const tmpRfValue = transformFn ? transformFn(value) : value;

        if (tmpRfValue.length === pattern.replace(/[+()\s-]/g, '').length) {
            value = this.maskService.applyMask(tmpRfValue, pattern);
            return prefix ? `${prefix}${value}` : value;
        }

        if (!foreignPattern) return value;

        if (foreignTransformFn) value = foreignTransformFn(value);

        value = this.maskService.applyMask(value, foreignPattern);

        return foreignPrefix ? `${foreignPrefix}${value}` : value;
    }
}
